import { operators as o } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';

export default {
    name: 'path-csv',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.PATHS,
    response: ({
        acrossApps,
        predecessors,
        gcsFileName,
    }: {
        acrossApps: boolean;
        predecessors: boolean;
        gcsFileName: string;
    }) => {
        const direction = predecessors ? 'Previous' : 'Next';

        return {
            location: 'gcs',
            mimeType: 'text/csv',
            name: `${gcsFileName}.csv`,
            fields: [
                { field: 'visitorId', label: 'Visitor ID' },
                { field: 'stepTime', label: 'Date' },
                { field: 'stepNum', label: 'Step Column' },
                { field: 'stepResourceName', label: 'Step' },
                acrossApps && { field: 'stepAppName', label: 'Step App' },
                { field: 'nextStepResourceName', label: `${direction} Step` },
                acrossApps && { field: 'nextStepAppName', label: `${direction} Step App` },
                { field: 'timeToNextStep', label: `Time ${predecessors ? 'from Previous' : 'to Next'} Step (ms)` },
                { field: 'untaggedUrl', label: 'Untagged URL' },
            ].filter(Boolean),
        };
    },
    build: ({ gcsFileName, predecessors }: { gcsFileName: string; predecessors: boolean }) => {
        return o.pipeline(
            {
                source: {
                    gcs: gcsFileName,
                },
            },
            o.filter('!isNil(visitorId)'),
            o.evaluate({
                pathCopy: 'path',
                one: '1',
            }),
            o.accumulate({
                uniquePathId: 'one',
            }),
            o.unwind('path', {
                index: 'pathIndex',
            }),
            o.evaluate({
                stepItems: 'path.items',
                nextStepItems: 'if(pathIndex + 1 == len(pathCopy), null, pathCopy[pathIndex + 1].items)',
                nextStepTime: 'if(pathIndex + 1 == len(pathCopy), -1, pathCopy[pathIndex + 1].time)',
                nextStepUnknown: 'if(pathIndex + 1 == len(pathCopy), -1, pathCopy[pathIndex + 1].unknown)',
                stepTime: 'path.time',
                untaggedUrl: 'if(isNil(path.unknown), "", path.unknown.url)',
            }),
            o.unwind('stepItems', {
                keepEmpty: true,
            }),
            o.evaluate({
                pageId: 'stepItems.pageId',
                featureId: 'stepItems.featureId',
                trackTypeId: 'stepItems.trackTypeId',
                guideId: 'stepItems.guideId',
            }),
            o.merge(
                ['pageId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.pages({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        pageId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['featureId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.features({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        featureId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['trackTypeId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.trackTypes({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        trackTypeId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['guideId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.guides({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        guideId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['appId'],
                o.mappings({
                    appName: 'appName',
                }),
                o.pipeline(
                    o.sources.apps(),
                    o.select({
                        appId: 'id',
                        appName: 'displayName + " - " + platform',
                    }),
                ),
            ),
            o.evaluate({
                resourceName: 'if(isNil(resourceName), "Untagged URL", resourceName)',
            }),
            o.group(
                ['visitorId', 'stepTime', 'uniquePathId'],
                o.groupField('resourceNames', o.list('resourceName')),
                o.groupField('appName', { first: 'appName' }),
                o.groupField('untaggedUrl', { first: 'untaggedUrl' }),
                o.groupField('pathIndex', { first: 'pathIndex' }),
                o.groupField('nextStepItems', { first: 'nextStepItems' }),
                o.groupField('nextStepTime', { first: 'nextStepTime' }),
                o.groupField('nextStepUnknown', { first: 'nextStepUnknown' }),
            ),
            o.unwind('nextStepItems', {
                keepEmpty: true,
            }),
            o.evaluate({
                pageId: 'nextStepItems.pageId',
                featureId: 'nextStepItems.featureId',
                trackTypeId: 'nextStepItems.trackTypeId',
                guideId: 'nextStepItems.guideId',
            }),
            o.merge(
                ['pageId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.pages({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        pageId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['featureId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.features({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        featureId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['trackTypeId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.trackTypes({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        trackTypeId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['guideId'],
                o.mappings({
                    resourceName: 'name',
                    appId: 'appId',
                }),
                o.pipeline(
                    o.sources.guides({
                        appId: 'expandAppIds("*")',
                    }),
                    o.select({
                        guideId: 'id',
                        name: 'name',
                        appId: 'appId',
                    }),
                ),
            ),
            o.merge(
                ['appId'],
                o.mappings({
                    nextAppName: 'appName',
                }),
                o.pipeline(
                    o.sources.apps(),
                    o.select({
                        appId: 'id',
                        appName: 'displayName + " - " + platform',
                    }),
                ),
            ),
            o.evaluate({
                resourceName: 'if(isObject(nextStepUnknown), "Untagged URL", resourceName)',
            }),
            o.evaluate({
                resourceName: predecessors
                    ? 'if(isNil(resourceName), "No previous step", resourceName)'
                    : 'if(isNil(resourceName), "No next step", resourceName)',
            }),
            o.group(
                ['visitorId', 'stepTime', 'uniquePathId'],
                o.groupField('nextResourceNames', o.list('resourceName')),
                o.groupField('resourceNames', { first: 'resourceNames' }),
                o.groupField('appName', { first: 'appName' }),
                o.groupField('nextAppName', { first: 'nextAppName' }),
                o.groupField('untaggedUrl', { first: 'untaggedUrl' }),
                o.groupField('pathIndex', { first: 'pathIndex' }),
                o.groupField('nextStepTime', { first: 'nextStepTime' }),
            ),
            o.evaluate({
                stepTime: predecessors
                    ? 'if(pathIndex == 0, stepTime - 1, stepTime)'
                    : 'if(pathIndex == 0, stepTime + 1, stepTime)',
            }),
            o.sort(['visitorId', 'stepTime']),
            o.evaluate({
                stepTime: predecessors
                    ? 'if(pathIndex == 0, stepTime + 1, stepTime)'
                    : 'if(pathIndex == 0, stepTime - 1, stepTime)',
            }),
            o.select({
                visitorId: 'visitorId',
                untaggedUrl: 'untaggedUrl',
                stepTime: 'formatTime("2006-01-02 15:04:05", stepTime)',
                stepNum: 'pathIndex + 1',
                stepResourceName: 'resourceNames',
                stepAppName: 'appName',
                nextStepResourceName: 'nextResourceNames',
                nextStepAppName: 'nextAppName',
                timeToNextStep: predecessors
                    ? 'if(nextStepTime == -1, null, stepTime - nextStepTime)'
                    : 'if(nextStepTime == -1, null, nextStepTime - stepTime)',
            }),
        );
    },
};
